import { Link } from "react-router-dom";
import { useReveal } from "../lib/useReveal";
import {
    ShieldCheck,
    Sparkles,
    MapPinned,
    PhoneCall,
    Lock,
    ArrowRight,
} from "lucide-react";
import HopeNamingNote from "./HopeNamingNote";
import HopeChatPreview from "./HopeChatPreview";
import { WAITLIST_HREF } from "../data/appLaunch";

export default function Hero() {
    const ref = useReveal();

    return (
        <section
            ref={ref}
            data-testid="hero-section"
            role="banner"
            className="relative overflow-hidden isolate"
        >
            <div className="absolute inset-0 z-0" aria-hidden="true">
                <div className="absolute inset-0 bg-[#070d22]" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.12)_0%,_transparent_36%),radial-gradient(circle_at_bottom_left,_rgba(34,211,238,0.06)_0%,_transparent_30%)]" />
                <div className="absolute inset-0 opacity-[0.05] bg-[linear-gradient(to_right,white_1px,transparent_1px),linear-gradient(to_bottom,white_1px,transparent_1px)] bg-[size:72px_72px]" />
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 md:pt-44 pb-24 md:pb-28">
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-10 xl:gap-14 items-center">
                    <div className="reveal order-2 lg:order-1 text-center lg:text-left text-white max-w-2xl mx-auto lg:mx-0">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[rgba(85,76,180,0.22)] backdrop-blur-md border border-white/10 text-sm font-medium text-white/90 mb-7 shadow-[0_10px_24px_rgba(13,18,44,0.25)]">
                            <Sparkles className="h-4 w-4 text-cyan-200" />
                            AI-Powered Private Recovery Support
                        </div>

                        <h1
                            id="hero-title"
                            className="text-[2.35rem] sm:text-[3rem] md:text-[3.5rem] xl:text-[3.75rem] font-bold tracking-[-0.03em] leading-[1.02] mb-7"
                        >
                            Built by people who&apos;ve been there.
                            <span className="block text-emerald-300 mt-2">
                                For the moments that actually matter.
                            </span>
                        </h1>

                        <p className="text-lg sm:text-[1.2rem] text-white/78 font-light leading-[1.5] max-w-xl mx-auto lg:mx-0 mb-5">
                            A safer, more supportive way to stay on the path — wherever you are.
                            Whether you&apos;re thinking about getting sober, on day one, or ten years
                            in.
                        </p>

                        <HopeNamingNote className="max-w-xl mx-auto lg:mx-0 mb-8 text-white/60" />

                        <div className="flex flex-col sm:flex-row flex-wrap justify-center lg:justify-start gap-3 mb-8">
                            <Link
                                to={WAITLIST_HREF}
                                data-testid="hero-waitlist-button"
                                className="inline-flex items-center justify-center gap-2 h-12 sm:h-14 px-7 rounded-full bg-white text-slate-900 hover:bg-white/92 text-base font-semibold shadow-xl transition-colors"
                            >
                                Join the launch waitlist
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                            <Link
                                to="/directory"
                                data-testid="hero-directory-button"
                                className="inline-flex items-center justify-center gap-2 h-12 sm:h-14 px-7 rounded-full border border-white/25 bg-white/8 backdrop-blur-sm text-white hover:bg-white/12 text-base font-semibold transition-colors"
                            >
                                <MapPinned className="w-4 h-4 text-emerald-300" />
                                Browse the directory
                            </Link>
                        </div>

                        <div
                            className="flex flex-wrap justify-center lg:justify-start items-center gap-3"
                            role="list"
                            aria-label="Hero feature chips"
                        >
                            {[
                                {
                                    Icon: ShieldCheck,
                                    label: "Private AI companion",
                                    tone: "text-cyan-200",
                                },
                                {
                                    Icon: Lock,
                                    label: "Fully encrypted journaling",
                                    tone: "text-amber-200",
                                },
                                {
                                    Icon: PhoneCall,
                                    label: "Judgment-free 24/7",
                                    tone: "text-fuchsia-200",
                                },
                            ].map((item) => (
                                <div
                                    key={item.label}
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/6 backdrop-blur-sm border border-white/10 text-white/88 shadow-[0_10px_20px_rgba(6,9,25,0.22)]"
                                    role="listitem"
                                >
                                    <item.Icon className={`w-4 h-4 ${item.tone}`} />
                                    <span className="text-sm font-medium">{item.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="order-1 lg:order-2">
                        <HopeChatPreview />
                    </div>
                </div>
            </div>
        </section>
    );
}
